import { useCallback, useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import { useDispatch, useSelector } from 'react-redux';

import { APP_NAME } from '@/constants';
import { fetchAuditableElements } from '@/slices/auditableElementsSlice';
import { clearOnLogOut, refreshSignIn } from '@/slices/authSlice';
import { RootState } from '@/store';

/**
 * Keeps Redux auth in sync with react-oidc-context silent renew events.
 */
export const useTokenAutoRefreshOIDC = () => {
  const dispatch = useDispatch();
  const { events, signinSilent, signoutSilent } = useAuth();

  const authSlice = useSelector((state: RootState) => state.auth);
  const { isOidcClient, idToken } = authSlice;

  const syncOidcUser = useCallback(
    (user: {
      access_token?: string;
      id_token?: string;
      refresh_token?: string;
      expires_at?: number;
    }) => {
      if (!user?.access_token) return;

      dispatch(
        refreshSignIn({
          accessToken: user.access_token,
          idToken: user.id_token,
          refreshToken: user.refresh_token,
          expiresIn: user.expires_at
        })
      );
      dispatch(
        fetchAuditableElements({
          applicationName: APP_NAME
        }) as any
      );
    },
    [dispatch]
  );

  const handleTokenExpiring = useCallback(async () => {
    try {
      const user = await signinSilent();
      if (user) {
        syncOidcUser(user);
      }
    } catch (err) {
      console.error('Error in handleTokenExpiring', err);
      try {
        await signoutSilent({
          id_token_hint: idToken
        });
      } catch {
        // ignore silent sign-out failures during forced cleanup
      }
      dispatch(clearOnLogOut() as any);
      localStorage.clear();
    }
  }, [dispatch, idToken, signinSilent, signoutSilent, syncOidcUser]);

  useEffect(() => {
    if (!isOidcClient || !events) return;

    events.addAccessTokenExpiring(handleTokenExpiring);
    events.addUserLoaded(syncOidcUser);

    return () => {
      events.removeAccessTokenExpiring(handleTokenExpiring);
      events.removeUserLoaded(syncOidcUser);
    };
  }, [events, handleTokenExpiring, isOidcClient, syncOidcUser]);
};
